import {
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
} from 'three';
import { addStaticCollider } from './util.js';
import { Parts } from './parts.js';

/**
 * Tea ladies on the east sidewalk of Street 60.
 *
 * A `bint al-shai` sets up on the pavement: a low table under a plastic floral
 * cloth, a row of small glasses, a charcoal stove with the kettle permanently
 * on it, a jerry can of water and a bowl to rinse the glasses in. She sits on a
 * bankar behind the table. Her regulars do not crowd her table — they take
 * their glass and sit a little away, two to a small table, each on their own
 * bankar.
 *
 * Everything except the fire and the steam is merged into a single vertex-
 * coloured mesh per stand, so a whole stand plus its two seating clusters costs
 * three draw calls.
 */

/** Mid-way between the streetlights, which run every 58 m from s = 24. */
const SITES = [1097, 1345, 1619, 2141, 3301, 4113];

/** East sidewalk centre sits at 12.6; pulled in so the stools clear the kerb. */
const SIDEWALK_LATERAL = 12.3;
const SIDEWALK_Y = 0.055;

/** The regulars' small tables, set back from hers along the pavement. */
const CLUSTER_Z = [-2.9, -5.4];

const COLORS = {
  wood: 0x8a6a45,
  cloth: 0xa8503f,
  steel: 0x6d6a62,
  glass: 0xd9e2dd,
  tea: 0x8f4a1c,
  sugar: 0xd9d3c4,
  daftar: 0xe4dcc6,
  mint: 0x4f7d4a,
  bowl: 0x9fb3a6,
  kettle: 0xb8b2a3,
  kanoon: 0x35302a,
  kanoonRim: 0x4c453c,
  can: 0x2f6b52,
  canDark: 0x24523f,
  water: 0x4a7d8c,
  table: 0xd9d3c4,
  skin: 0x6a4b36,
  shoe: 0x4a3a2c,
};

/** Pale galabeyas and tobes for the regulars. */
const ROBES = [0xe6dfcc, 0xc7d4cd, 0xd8c3a6, 0xaebfd0, 0xc9b9d2, 0xbfae9a];
const HEADWRAPS = [0xe8e2d2, 0xd6cbb2, 0xcfd8d2, 0xdccdb4, 0xd2c6d6, 0xcdbfa8];
const HER_ROBE = 0xc25a4a;
const HER_WRAP = 0xe8dcc4;

export function buildTeaStands(level, route) {
  const bodyMaterial = new MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.92,
    metalness: 0.03,
  });
  const fireMaterial = new MeshBasicMaterial({ color: 0xe8892f, toneMapped: false });
  const steamGeometry = buildSteamGeometry();

  const stands = [];
  const puffs = [];

  SITES.forEach((s, index) => {
    const frame = route.offsetFrame(s, SIDEWALK_LATERAL);
    const group = new Group();
    group.position.set(frame.position.x, SIDEWALK_Y, frame.position.z);
    group.rotation.y = frame.yaw;
    group.name = `tea-stand-${Math.round(s)}`;
    level.root.add(group);

    const parts = new Parts();
    buildStand(parts, index);
    CLUSTER_Z.forEach((zc, clusterIndex) => {
      buildCluster(parts, zc, index * 2 + clusterIndex);
    });

    const merged = parts.merge();
    if (merged) {
      const body = new Mesh(merged, bodyMaterial);
      body.castShadow = true;
      body.receiveShadow = true;
      body.name = 'tea-stand-body';
      group.add(body);
    }

    // The charcoal fire stays its own unlit mesh so it reads as a glow rather
    // than as a bright-coloured object.
    const fire = new Mesh(new CylinderGeometry(0.132, 0.132, 0.022, 12), fireMaterial);
    fire.position.set(0.40, 0.437, 0.95);
    group.add(fire);

    // One puff plume per kettle, animated in `update`.
    const steam = new Mesh(
      steamGeometry,
      new MeshBasicMaterial({
        color: 0xf6efe2,
        transparent: true,
        opacity: 0.14,
        depthWrite: false,
      }),
    );
    steam.position.set(0.29, 0.70, 0.95);
    steam.renderOrder = 2;
    group.add(steam);
    puffs.push({ mesh: steam, phase: index * 0.31 });

    // Her table and herself.
    const standCentre = frame.position
      .clone()
      .addScaledVector(frame.right, 0.2)
      .addScaledVector(frame.tangent, -0.15);
    addStaticCollider(level, {
      x: standCentre.x,
      y: 0.55,
      z: standCentre.z,
      width: 1.25,
      height: 1.1,
      depth: 1.7,
      yaw: frame.yaw,
    });

    // The regulars' clusters, so the truck cannot be driven through them.
    for (const zc of CLUSTER_Z) {
      const clusterCentre = frame.position.clone().addScaledVector(frame.tangent, -zc);
      addStaticCollider(level, {
        x: clusterCentre.x,
        y: 0.5,
        z: clusterCentre.z,
        width: 0.85,
        height: 1.0,
        depth: 1.55,
        yaw: frame.yaw,
      });
    }

    stands.push({
      s,
      group,
      position: frame.position.clone(),
      yaw: frame.yaw,
      purchases: 0,
    });
  });

  let time = 0;

  return {
    stands,
    sites: SITES,
    clusters: CLUSTER_Z.length,
    update(dt) {
      time += dt;
      for (const puff of puffs) {
        const t = (time * 0.42 + puff.phase) % 1;
        puff.mesh.position.y = 0.70 + t * 0.62;
        puff.mesh.scale.setScalar(0.7 + t * 0.9);
        puff.mesh.material.opacity = 0.17 * Math.sin(Math.PI * t);
      }
    },
  };
}

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

function buildStand(parts, index) {
  // Tabliya: low table under a plastic floral cloth.
  parts.box(0.62, 0.05, 1.10, COLORS.wood, 0, 0.705, 0);
  parts.box(0.68, 0.13, 1.16, COLORS.cloth, 0, 0.645, 0);
  for (const x of [-0.26, 0.26]) {
    for (const z of [-0.48, 0.48]) {
      parts.box(0.045, 0.70, 0.045, COLORS.steel, x, 0.35, z);
    }
  }

  // The row of small glasses, each with tea already in it.
  for (let i = 0; i < 6; i += 1) {
    const z = -0.42 + i * 0.16;
    parts.cyl(0.031, 0.027, 0.095, 8, COLORS.glass, -0.17, 0.7775, z);
    parts.cyl(0.024, 0.022, 0.055, 8, COLORS.tea, -0.17, 0.7575, z);
  }

  // Sugar tin, bowl of mint, and the daftar she writes tabs in.
  parts.cyl(0.062, 0.062, 0.13, 10, COLORS.sugar, 0.13, 0.795, 0.4);
  parts.cyl(0.075, 0.052, 0.06, 10, COLORS.bowl, 0.14, 0.76, 0.16);
  for (const [x, z] of [[0.12, 0.14], [0.16, 0.17], [0.15, 0.12]]) {
    parts.sphere(0.018, 5, 4, COLORS.mint, x, 0.795, z);
  }
  parts.box(0.13, 0.014, 0.17, COLORS.daftar, 0.15, 0.737, -0.32);

  // Her bankar, then herself seated on it facing the road.
  parts.group(0.52, 0, 0.02, Math.PI / 2, () => {
    addSeatedFigure(parts, { robe: HER_ROBE, wrap: HER_WRAP, holdsGlass: false });
  });

  // Kanoon with the kettle permanently on it.
  parts.cyl(0.155, 0.17, 0.4, 12, COLORS.kanoon, 0.4, 0.2, 0.95);
  parts.cyl(0.175, 0.175, 0.035, 12, COLORS.kanoonRim, 0.4, 0.415, 0.95);
  parts.cyl(0.115, 0.14, 0.2, 12, COLORS.kettle, 0.4, 0.53, 0.95);
  parts.cyl(0.055, 0.08, 0.045, 10, COLORS.kettle, 0.4, 0.6525, 0.95);
  parts.sphere(0.022, 6, 5, COLORS.kanoon, 0.4, 0.678, 0.95);
  parts.cyl(0.016, 0.026, 0.17, 6, COLORS.kettle, 0.29, 0.575, 0.95, { z: 1.15 });

  // Jerry can and the bowl the glasses are rinsed in.
  parts.box(0.19, 0.4, 0.14, COLORS.can, 0.62, 0.2, -0.62);
  parts.cyl(0.032, 0.032, 0.035, 8, COLORS.canDark, 0.66, 0.417, -0.62);
  parts.box(0.1, 0.025, 0.03, COLORS.canDark, 0.6, 0.415, -0.62);
  parts.cyl(0.17, 0.125, 0.12, 12, COLORS.can, 0.28, 0.06, -0.95);
  parts.cyl(0.155, 0.155, 0.012, 12, COLORS.water, 0.28, 0.115, -0.95);
}

function buildCluster(parts, zc, seed) {
  const robeA = ROBES[(seed + 1) % ROBES.length];
  const robeB = ROBES[(seed + 3) % ROBES.length];
  const wrapA = HEADWRAPS[(seed + 1) % HEADWRAPS.length];
  const wrapB = HEADWRAPS[(seed + 3) % HEADWRAPS.length];

  parts.box(0.52, 0.04, 0.52, COLORS.table, 0, 0.54, zc);
  for (const x of [-0.21, 0.21]) {
    for (const dz of [-0.21, 0.21]) {
      parts.box(0.035, 0.52, 0.035, COLORS.steel, x, 0.26, zc + dz);
    }
  }

  for (const x of [-0.09, 0.09]) {
    parts.cyl(0.03, 0.026, 0.09, 8, COLORS.glass, x, 0.605, zc);
    parts.cyl(0.023, 0.021, 0.05, 8, COLORS.tea, x, 0.59, zc);
  }

  // Two to a table, facing each other across it.
  parts.group(0, 0, zc - 0.62, Math.PI, () => {
    addSeatedFigure(parts, { robe: robeA, wrap: wrapA, holdsGlass: true });
  });
  parts.group(0, 0, zc + 0.62, 0, () => {
    addSeatedFigure(parts, { robe: robeB, wrap: wrapB, holdsGlass: true });
  });
}

/**
 * A seated figure on a bankar. Built facing local -Z, so the caller rotates it
 * towards whatever it is sitting around.
 */
function addSeatedFigure(parts, { robe, wrap, holdsGlass }) {
  // Bankar.
  parts.box(0.34, 0.045, 0.34, COLORS.wood, 0, 0.3, 0.05);
  for (const x of [-0.14, 0.14]) {
    for (const z of [-0.09, 0.19]) {
      parts.box(0.035, 0.3, 0.035, COLORS.wood, x, 0.15, z);
    }
  }

  // Body: a tapered robe, head, and a headwrap.
  parts.cyl(0.17, 0.245, 0.58, 9, robe, 0, 0.62, 0.05);
  parts.sphere(0.105, 10, 8, COLORS.skin, 0, 1.0, 0.05);
  parts.cyl(0.108, 0.112, 0.075, 10, wrap, 0, 1.055, 0.05);

  // Thighs, shins and feet pointing forward.
  parts.box(0.32, 0.13, 0.3, robe, 0, 0.375, -0.1);
  parts.box(0.3, 0.33, 0.13, robe, 0, 0.165, -0.26);
  parts.box(0.3, 0.055, 0.16, COLORS.shoe, 0, 0.028, -0.32);

  // Arms resting at the sides.
  for (const x of [-0.175, 0.175]) {
    parts.cyl(0.042, 0.05, 0.36, 6, robe, x, 0.7, 0);
  }

  if (holdsGlass) {
    parts.cyl(0.03, 0.026, 0.09, 8, COLORS.glass, 0.19, 0.53, -0.02);
  }
}

function buildSteamGeometry() {
  const parts = new Parts();
  parts.sphere(0.055, 6, 5, 0xffffff, 0, 0, 0);
  parts.sphere(0.07, 6, 5, 0xffffff, 0.025, 0.13, 0.012);
  parts.sphere(0.085, 6, 5, 0xffffff, -0.012, 0.27, 0);
  return parts.merge();
}
